/**
 * ax-slider-categories.js
 * Custom element <ax-slider-categories> : slider de cartes de catégories.
 *
 * Adapté du B2C (theme-nutrimuscle-japan : _dev/js/sections/slider-categories.js),
 * dont l'infrastructure (classe AxSwiper, SectionObserver, window.assetsUrl,
 * build webpack) n'existe pas dans ce thème. Ici le composant est autonome et
 * ne dépend que de window.Swiper, chargé globalement par layout/theme.liquid.
 *
 * Convention du thème : les options sont passées en JSON par l'attribut
 * data-options du conteneur .swiper, avec des clés métier que ce fichier
 * traduit en configuration Swiper.
 *
 * @class AxSliderCategories
 * @extends HTMLElement
 */

/**
 * Garantit la disponibilité de window.Swiper.
 *
 * Swiper est chargé pour tous les templates par layout/theme.liquid : ce filet
 * ne sert qu'aux rendus tardifs (Section Rendering API, éditeur de thème) où le
 * script global n'aurait pas encore été exécuté.
 *
 * @returns {Promise<void>}
 */
function ensureSwiper() {
	return new Promise((resolve) => {
		if (typeof window.Swiper !== 'undefined') return resolve();

		// Les URL sont posees par layout/theme.liquid : sans elles, rien a injecter.
		if (!window.axSwiperAssets) {
			console.error('ensureSwiper: window.axSwiperAssets absent, Swiper introuvable');
			return resolve();
		}

		const existing = document.querySelector('script[data-ax-swiper]');
		if (existing) {
			existing.addEventListener('load', () => resolve(), { once: true });
			return;
		}

		if (!document.querySelector('link[data-ax-swiper]')) {
			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = window.axSwiperAssets.css;
			link.dataset.axSwiper = '';
			document.head.appendChild(link);
		}

		const script = document.createElement('script');
		script.src = window.axSwiperAssets.js;
		script.dataset.axSwiper = '';
		script.addEventListener('load', () => resolve(), { once: true });
		document.head.appendChild(script);
	});
}

class AxSliderCategories extends HTMLElement {
	/** @type {{ swiper: string, slide: string }} */
	#selectors = {
		swiper: '.swiper',
		slide: '.swiper-slide',
	};

	/** @type {{ locked: string }} */
	#classes = {
		locked: 'is-locked',
	};

	/** @type {{ swiper: HTMLElement|null }} */
	#elements = {};

	/** @type {object|null} Instance Swiper, conservée pour le nettoyage. */
	#swiper = null;

	/** @type {MediaQueryList|null} Non nul quand la grille mobile est activée. */
	#desktopQuery = null;

	/**
	 * Lifecycle : montage dans le DOM.
	 * @private
	 */
	connectedCallback() {
		this.#cacheElements();
		if (!this.#isValidSetup()) return;

		if (this.#readOptions().stackMobile) {
			this.#desktopQuery = window.matchMedia('(min-width: 750px)');
			this.#desktopQuery.addEventListener('change', this.#syncSlider);
		}

		ensureSwiper().then(this.#syncSlider);
	}

	/**
	 * Lifecycle : retrait du DOM. Swiper pose des écouteurs sur window et un
	 * ResizeObserver : sans destroy() ils survivent au retrait de la section
	 * dans l'éditeur de thème.
	 * @private
	 */
	disconnectedCallback() {
		if (this.#desktopQuery) {
			this.#desktopQuery.removeEventListener('change', this.#syncSlider);
		}

		if (this.#swiper) {
			this.#swiper.destroy(true, true);
			this.#swiper = null;
		}
	}

	/**
	 * Met en cache les références DOM.
	 * @private
	 */
	#cacheElements() {
		this.#elements = {
			swiper: this.querySelector(this.#selectors.swiper),
		};
	}

	/**
	 * Vérifie que le composant a de quoi fonctionner.
	 * @private
	 * @returns {boolean}
	 */
	#isValidSetup() {
		if (!this.#elements.swiper) {
			console.error('AxSliderCategories: conteneur .swiper introuvable');
			return false;
		}

		if (this.querySelectorAll(this.#selectors.slide).length === 0) {
			console.error('AxSliderCategories: aucune slide à afficher');
			return false;
		}

		return true;
	}

	/**
	 * Lit les options métier posées par le Liquid.
	 * @private
	 * @returns {object}
	 */
	#readOptions() {
		const raw = this.#elements.swiper.dataset.options;
		if (!raw) return {};

		try {
			return JSON.parse(raw);
		} catch (error) {
			console.error('AxSliderCategories: data-options illisible', error);
			return {};
		}
	}

	/**
	 * Active le slider sur ordinateur, le détruit quand la grille mobile prend
	 * le relais. `destroy(true, true)` retire les styles inline de Swiper, sans
	 * quoi les largeurs de slides survivraient à la grille CSS.
	 * @private
	 */
	#syncSlider = () => {
		if (!this.#desktopQuery || this.#desktopQuery.matches) {
			if (!this.#swiper) this.#initSwiper();
			return;
		}

		if (this.#swiper) {
			this.#swiper.destroy(true, true);
			this.#swiper = null;
		}
	};

	/**
	 * Initialise Swiper.
	 * @private
	 */
	#initSwiper() {
		if (typeof window.Swiper === 'undefined') return;

		const options = this.#readOptions();

		this.#swiper = new window.Swiper(this.#elements.swiper, {
			// Largeurs calculées par Swiper depuis le nombre de colonnes plutôt
			// que par des classes de grille : cumuler les deux fait dépasser la
			// largeur totale du conteneur et fait apparaître une navigation
			// alors que tout tient.
			slidesPerView: options.columnsMobile || 2,
			breakpoints: {
				990: {
					slidesPerView: options.columnsDesktop || 6,
					freeMode: false,
				},
			},
			slidesPerGroup: options.slidesToMove || 1,
			spaceBetween: this.#readGap(),
			speed: options.speed || 300,
			// Défilement libre sur mobile : la maquette ne prévoit ni flèches ni
			// pagination, le glissement au doigt est la seule affordance.
			freeMode: options.freeModeMobile === true,
			grabCursor: true,
			// Désactive le slider quand toutes les cartes tiennent déjà.
			watchOverflow: true,
			// Recalcule après chargement des images et des polices : sans ça les
			// dimensions restent figées sur un contenu incomplet.
			observer: true,
			observeParents: true,
			observeSlideChildren: true,
			a11y: {
				enabled: true,
			},
			on: {
				// Reflete l'etat de verrouillage sur l'hote : le curseur de
				// glissement ne doit apparaitre que si le slider defile
				// vraiment. Swiper emet ces evenements a l'init et a chaque
				// recalcul (resize, images chargees).
				lock: () => this.#setLocked(true),
				unlock: () => this.#setLocked(false),
			},
		});

		// Les evenements lock/unlock ne sont pas garantis a l'init selon le
		// nombre de slides : on pose l'etat initial explicitement.
		this.#setLocked(this.#swiper.isLocked === true);
	}

	/**
	 * Pose ou retire la classe d'etat de verrouillage.
	 * @private
	 * @param {boolean} isLocked
	 */
	#setLocked(isLocked) {
		this.classList.toggle(this.#classes.locked, isLocked);
	}

	/**
	 * Lit l'écart entre cartes depuis la variable CSS de la section, pour que le
	 * réglage du schéma reste la source unique de vérité.
	 * @private
	 * @returns {number} Écart en pixels.
	 */
	#readGap() {
		const raw = getComputedStyle(this).getPropertyValue('--ax-cat-gap');
		const parsed = parseFloat(raw);
		return Number.isFinite(parsed) ? parsed : 18;
	}
}

if (!customElements.get('ax-slider-categories')) {
	customElements.define('ax-slider-categories', AxSliderCategories);
}
